import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, Search, Filter, Download, CheckSquare, Square,
  UserPlus, AlertTriangle, RefreshCw, Eye 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const TRCDevicesBulk = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Bulk action states
  const [bulkAction, setBulkAction] = useState('');
  const [selectedEngineer, setSelectedEngineer] = useState('');
  const [assignPriority, setAssignPriority] = useState('normal');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/trc/devices`
        : `${API_URL}/api/trc/devices?status=${statusFilter}`;
      
      const [devicesRes, engRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/trc/engineer/list`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: { engineers: [] } }))
      ]);
      
      if (devicesRes.data.success) {
        setDevices(devicesRes.data.devices);
      }
      
      if (engRes.data.engineers) {
        setEngineers(engRes.data.engineers);
      }
      
      setSelectedDevices([]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDeviceSelection = (deviceId) => {
    setSelectedDevices(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDevices.length === filteredDevices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(filteredDevices.map(d => d.trc_device_id));
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedEngineer || selectedDevices.length === 0) {
      alert('Please select devices and an engineer');
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/trc/devices/bulk/assign`,
        {
          device_ids: selectedDevices,
          engineer_id: selectedEngineer,
          priority: assignPriority
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Successfully assigned ${response.data.assigned_count} devices!`);
        setSelectedDevices([]);
        setShowBulkActions(false);
        fetchData();
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign devices');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkSOS = async (isSOS) => {
    if (selectedDevices.length === 0) {
      alert('Please select devices');
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/trc/devices/bulk/update-sos`,
        {
          device_ids: selectedDevices,
          is_sos: isSOS
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Successfully updated ${response.data.updated_count} devices!`);
        setSelectedDevices([]);
        fetchData();
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update SOS status');
    } finally {
      setProcessing(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/trc/devices/bulk/export`,
        {
          device_ids: selectedDevices.length > 0 ? selectedDevices : null,
          status_filter: statusFilter !== 'all' ? statusFilter : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Convert to CSV and download
        const csv = response.data.data.map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `trc-devices-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
      }
    } catch (error) {
      alert('Failed to export data');
    }
  };

  const filteredDevices = devices.filter(device =>
    device.did.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.model.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      'received': 'bg-blue-100 text-blue-800',
      'binned': 'bg-purple-100 text-purple-800',
      'assigned_to_engineer': 'bg-yellow-100 text-yellow-800',
      'rfr': 'bg-cyan-100 text-cyan-800',
      'repair_in_progress': 'bg-amber-100 text-amber-800',
      'repair_completed': 'bg-teal-100 text-teal-800',
      'fqc_pass': 'bg-green-100 text-green-800',
      'fqc_fail_rtr': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
            <Package className="w-8 h-8 mr-3" />
            TRC Devices Management
          </h1>
          <p className="text-gray-600 mt-1">
            Bulk device actions and management
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button
            onClick={fetchData}
            className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: ZOBOX_BLUE }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Selection Info */}
      {selectedDevices.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-blue-800 font-medium">
              {selectedDevices.length} device(s) selected
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4 inline mr-2" />
                Bulk Assign
              </button>
              <button
                onClick={() => handleBulkSOS(true)}
                disabled={processing}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Mark SOS
              </button>
              <button
                onClick={() => handleBulkSOS(false)}
                disabled={processing}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                Remove SOS
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Panel */}
      {showBulkActions && (
        <div className="bg-white border rounded-lg p-6 shadow-md">
          <h3 className="text-lg font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            Bulk Assign Devices
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Engineer
              </label>
              <select
                value={selectedEngineer}
                onChange={(e) => setSelectedEngineer(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
              >
                <option value="">Choose engineer...</option>
                {engineers.map(eng => (
                  <option key={eng.engineer_id} value={eng.engineer_id}>
                    {eng.name} ({eng.workload || 0}/25)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={assignPriority}
                onChange={(e) => setAssignPriority(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
              >
                <option value="normal">Normal</option>
                <option value="sos">SOS Priority</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleBulkAssign}
                disabled={processing || !selectedEngineer}
                className="w-full px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: ZOBOX_BLUE }}
              >
                {processing ? 'Assigning...' : `Assign ${selectedDevices.length} Devices`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
            >
              <option value="all">All Status</option>
              <option value="received">Received</option>
              <option value="binned">Binned</option>
              <option value="rfr">Ready for Repair</option>
              <option value="assigned_to_engineer">Assigned</option>
              <option value="repair_in_progress">In Progress</option>
              <option value="repair_completed">Completed</option>
              <option value="fqc_pass">FQC Pass</option>
            </select>
          </div>
          <div>
            <button
              onClick={toggleSelectAll}
              className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50 font-medium"
              style={{ color: ZOBOX_BLUE }}
            >
              {selectedDevices.length === filteredDevices.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>
      </div>

      {/* Devices Table */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedDevices.length === filteredDevices.length && filteredDevices.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Device ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Device Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Engineer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredDevices.map((device, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedDevices.includes(device.trc_device_id)}
                      onChange={() => toggleDeviceSelection(device.trc_device_id)}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {device.did}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div>
                      <p className="font-medium">{device.brand} {device.model}</p>
                      <p className="text-gray-500 text-xs">{device.category}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(device.status)}`}>
                      {device.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {device.assigned_engineer || '-'}
                  </td>
                  <td className="px-6 py-4">
                    {device.is_sos && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                        SOS
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => navigate(`/device/${device.trc_device_id}`)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: ZOBOX_BLUE }}></div>
          </div>
        )}

        {!loading && filteredDevices.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No devices found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TRCDevicesBulk;
